import { ArrowDown, ArrowLeft, ArrowUp, Pencil, Play, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  useDeletePlaylistMutation,
  usePlaylistViewQuery,
  useReplacePlaylistItemsMutation,
  useUpdatePlaylistMutation,
  type PlaylistView,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogTitle,
  Field,
  FieldError,
  FieldLabel,
  Input,
  StateSurface,
  Textarea,
} from '../../design-system/index.js';
import {
  PlaybackButton,
  usePlaybackControl,
  usePlaybackTransport,
  type PlaybackItem,
} from '../../player/index.js';
import { formatDuration, PageHeader } from '../shared/page.js';
import { playbackItemFromTrack } from '../tracks/track-presentation.js';

type PlaylistTrack = PlaylistView['items'][number];

export function PlaylistDetailPage() {
  const { id } = useParams();
  const { library } = useWorkspace();
  const view = usePlaylistViewQuery(library.id, id);
  const replace = useReplacePlaylistItemsMutation();
  const removePlaylist = useDeletePlaylistMutation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const player = usePlaybackTransport();
  const [undo, setUndo] = useState<{ trackId: string; index: number; name: string } | null>(null);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const tracks = view.data?.items ?? [];
  const playbackItems = useMemo(
    () => tracks.flatMap(({ track }) => playbackItemFromTrack(track) ?? []),
    [tracks],
  );

  if (view.isLoading) return <StateSurface kind="loading" title="Loading playlist" />;
  if (view.error)
    return (
      <StateSurface
        kind="error"
        title="Playlist unavailable"
        action={
          <Button variant="secondary" onClick={() => void view.refetch()}>
            Try again
          </Button>
        }
      />
    );
  if (!view.data) return <StateSurface title="Playlist not found" />;
  const { playlist } = view.data;
  const playbackContext = {
    kind: 'playlist' as const,
    id: `playlist:${playlist.id}:${playlist.revision}`,
    label: playlist.name,
  };

  const commitOrder = async (trackIds: string[]) => {
    await replace.mutateAsync({
      libraryId: library.id,
      playlistId: playlist.id,
      body: { expected_revision: playlist.revision, track_ids: trackIds },
    });
    await queryClient.invalidateQueries({ queryKey: ['playlist', library.id, playlist.id] });
  };

  const move = (index: number, offset: -1 | 1) => {
    const next = tracks.map((item) => item.track.id);
    const target = index + offset;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target]!, next[index]!];
    void commitOrder(next);
  };

  return (
    <div>
      <Link
        to="/playlists"
        className="mb-7 inline-flex min-h-11 items-center gap-2 rounded-sm text-sm text-ink-2 outline-none hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
      >
        <ArrowLeft className="size-4" />
        Playlists
      </Link>
      <PageHeader
        eyebrow="Playlist"
        title={playlist.name}
        description={
          playlist.description ??
          `${tracks.length} managed ${tracks.length === 1 ? 'track' : 'tracks'}.`
        }
        action={
          <div className="flex flex-wrap gap-2">
            {playbackItems.length ? (
              <Button
                aria-label={`Play ${playlist.name} from beginning`}
                onClick={() => void player.playQueue(playbackItems, 0, playbackContext)}
              >
                <Play />
                Play
              </Button>
            ) : null}
            <Button variant="secondary" onClick={() => setEditing(true)}>
              <Pencil />
              Edit
            </Button>
            <Button variant="ghost" className="text-err" onClick={() => setConfirmDelete(true)}>
              <Trash2 />
              Delete
            </Button>
          </div>
        }
      />

      {!tracks.length ? (
        <StateSurface title="This playlist is empty">
          Add imported tracks from their Track detail page.
        </StateSurface>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border bg-surface">
          {tracks.map((item, index) => (
            <PlaylistRow
              key={item.track.id}
              item={item}
              index={index}
              total={tracks.length}
              pending={replace.isPending}
              playbackItem={playbackItemFromTrack(item.track)}
              onStart={() => {
                const startIndex = playbackItems.findIndex(
                  (playbackItem) => playbackItem.id === item.track.id,
                );
                if (startIndex >= 0) {
                  return player.playQueue(playbackItems, startIndex, playbackContext);
                }
              }}
              onMove={(offset) => move(index, offset)}
              onRemove={() => {
                const name =
                  item.track.display_metadata.title ?? item.track.provider_title ?? 'Untitled';
                setUndo({ trackId: item.track.id, index, name });
                void commitOrder(
                  tracks
                    .filter((_, itemIndex) => itemIndex !== index)
                    .map((entry) => entry.track.id),
                );
              }}
            />
          ))}
        </div>
      )}
      {replace.error ? (
        <p className="mt-4 text-sm text-err" role="alert">
          The playlist order could not be saved. Refresh and try again.
        </p>
      ) : null}
      {undo ? (
        <div
          role="status"
          className="fixed right-4 bottom-4 left-4 z-40 flex items-center gap-3 rounded-md border border-border-hi bg-surface-hi px-4 py-3 shadow-[var(--shadow)] sm:left-auto sm:min-w-80"
        >
          <p className="min-w-0 flex-1 truncate text-sm">Removed “{undo.name}”</p>
          <Button
            size="small"
            variant="ghost"
            onClick={() => {
              const next = tracks.map((item) => item.track.id);
              next.splice(undo.index, 0, undo.trackId);
              void commitOrder(next);
              setUndo(null);
            }}
          >
            Undo
          </Button>
          <button
            className="grid size-11 place-items-center rounded-sm text-ink-3 outline-none hover:bg-bg hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring"
            onClick={() => setUndo(null)}
            aria-label="Dismiss"
          >
            <X className="size-4" />
          </button>
        </div>
      ) : null}
      <EditPlaylistDialog
        open={editing}
        onOpenChange={setEditing}
        libraryId={library.id}
        playlist={playlist}
        onSaved={() => void view.refetch()}
      />
      <Dialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <DialogContent>
          <DialogTitle>Delete “{playlist.name}”?</DialogTitle>
          <DialogDescription>
            This removes only the playlist and its membership. Managed WAV files remain in your
            Library.
          </DialogDescription>
          {removePlaylist.error ? (
            <FieldError>The playlist could not be deleted.</FieldError>
          ) : null}
          <div className="flex justify-end gap-2">
            <DialogClose asChild>
              <Button variant="secondary">Cancel</Button>
            </DialogClose>
            <Button
              variant="danger"
              disabled={removePlaylist.isPending}
              onClick={async () => {
                await removePlaylist.mutateAsync({
                  libraryId: library.id,
                  playlistId: playlist.id,
                });
                await queryClient.invalidateQueries({ queryKey: ['playlists', library.id] });
                navigate('/playlists', { replace: true });
              }}
            >
              {removePlaylist.isPending ? 'Deleting…' : 'Delete playlist'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PlaylistRow({
  index,
  item,
  onMove,
  onStart,
  onRemove,
  pending,
  playbackItem,
  total,
}: {
  index: number;
  item: PlaylistTrack;
  onMove: (offset: -1 | 1) => void;
  onStart: () => Promise<void> | void;
  onRemove: () => void;
  pending: boolean;
  playbackItem: PlaybackItem | null;
  total: number;
}) {
  const { track } = item;
  const title = track.display_metadata.title ?? track.provider_title ?? 'Untitled';
  const playback = usePlaybackControl(playbackItem, onStart);
  return (
    <article className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-2 border-b border-border p-3 last:border-b-0 sm:grid-cols-[auto_auto_minmax(0,1fr)_auto] sm:px-4">
      <span className="hidden w-6 text-center font-mono text-xs text-ink-3 sm:block">
        {index + 1}
      </span>
      <PlaybackButton
        control={playback}
        className="grid size-11 place-items-center rounded-full border border-border-hi text-accent outline-none hover:bg-accent-soft focus-visible:ring-2 focus-visible:ring-accent-ring"
        size="icon"
        unavailableLabel={`Playback unavailable for ${title}`}
        variant="secondary"
      />
      <div className="min-w-0">
        <Link
          to={`/tracks/${track.id}`}
          className="truncate text-sm font-medium text-ink hover:text-accent"
        >
          {title}
        </Link>
        <p className="mt-1 font-mono text-xs text-ink-3">
          {track.display_metadata.artist ?? 'Unknown artist'} ·{' '}
          {formatDuration(track.duration_seconds)}
        </p>
      </div>
      <div className="col-span-2 flex items-center justify-end sm:col-span-1">
        <button
          disabled={pending || index === 0}
          className="grid size-11 place-items-center rounded-sm text-ink-3 outline-none hover:bg-bg hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring disabled:opacity-30"
          onClick={() => onMove(-1)}
          aria-label="Move up"
        >
          <ArrowUp className="size-4" />
        </button>
        <button
          disabled={pending || index === total - 1}
          className="grid size-11 place-items-center rounded-sm text-ink-3 outline-none hover:bg-bg hover:text-ink focus-visible:ring-2 focus-visible:ring-accent-ring disabled:opacity-30"
          onClick={() => onMove(1)}
          aria-label="Move down"
        >
          <ArrowDown className="size-4" />
        </button>
        <button
          disabled={pending}
          className="grid size-11 place-items-center rounded-sm text-ink-3 outline-none hover:bg-[color-mix(in_oklch,var(--err)_10%,transparent)] hover:text-err focus-visible:ring-2 focus-visible:ring-accent-ring"
          onClick={onRemove}
          aria-label={`Remove ${title}`}
        >
          <X className="size-4" />
        </button>
      </div>
    </article>
  );
}

function EditPlaylistDialog({
  libraryId,
  onOpenChange,
  onSaved,
  open,
  playlist,
}: {
  libraryId: string;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
  open: boolean;
  playlist: PlaylistView['playlist'];
}) {
  const update = useUpdatePlaylistMutation();
  const [name, setName] = useState(playlist.name);
  const [description, setDescription] = useState(playlist.description ?? '');
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogTitle>Edit playlist</DialogTitle>
        <form
          className="grid gap-4"
          onSubmit={async (event) => {
            event.preventDefault();
            await update.mutateAsync({
              libraryId,
              playlistId: playlist.id,
              body: {
                name: name.trim(),
                description: description.trim() || null,
                expected_revision: playlist.revision,
              },
            });
            onSaved();
            onOpenChange(false);
          }}
        >
          <Field>
            <FieldLabel htmlFor="edit-playlist-name">Name</FieldLabel>
            <Input
              id="edit-playlist-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
            />
          </Field>
          <Field>
            <FieldLabel htmlFor="edit-playlist-description">Description</FieldLabel>
            <Textarea
              id="edit-playlist-description"
              rows={3}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Field>
          {update.error ? <FieldError>The playlist changes could not be saved.</FieldError> : null}
          <div className="flex justify-end">
            <Button type="submit" disabled={!name.trim() || update.isPending}>
              {update.isPending ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
